import { useEffect, useRef } from "react";
import { getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { dbTasks, dbNotes, dbAnnouncements } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { TaskDoc } from "../types";

/** Get a weekly key for dedup (resets each week) */
function getWeekKey(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const week = Math.ceil(
    ((now.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7
  );
  return `${now.getFullYear()}_w${week}`;
}

/** Get today's date key */
function getDayKey(): string {
  return new Date().toISOString().split("T")[0];
}

/** Calculate consecutive-day streak from completed tasks */
function calculateStreak(tasks: TaskDoc[]): number {
  const completedDates = new Set<string>();
  for (const t of tasks) {
    if (t.toggle && t.completedDate) {
      completedDates.add(t.completedDate.split("T")[0]);
    }
  }

  let streak = 0;
  const date = new Date();
  // Start from yesterday (today might not be over yet)
  date.setDate(date.getDate() - 1);

  while (true) {
    const key = date.toISOString().split("T")[0];
    if (completedDates.has(key)) {
      streak++;
      date.setDate(date.getDate() - 1);
    } else {
      break;
    }
  }

  // Also check if the user has completed something today
  const todayKey = new Date().toISOString().split("T")[0];
  if (completedDates.has(todayKey)) {
    streak++;
  }

  return streak;
}

const useEngagementNotifications = (): void => {
  const userContext = useUser();
  const userDetails = userContext?.userDetails ?? null;
  const { addNotification } = useNotifications();
  const { settings } = useUserSettings();
  const hasRun = useRef(false);

  useEffect(() => {
    if (!settings.notifications) return;
    if (!userDetails?.user?.uid) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const uid = userDetails.user.uid;

    // Delay 3s to avoid competing with task notifications on app load
    const timer = setTimeout(async () => {
      try {
        // ── Fetch user's tasks and notes ──
        const [taskSnap, noteSnap] = await Promise.all([
          getDocs(query(dbTasks, where("userId", "==", uid))),
          getDocs(query(dbNotes, where("userId", "==", uid))),
        ]);

        const weekKey = getWeekKey();
        const dayKey = getDayKey();
        const np = settings.notificationPrefs ?? { tasks: true, workouts: true, social: true, announcements: true, engagement: true };

        // ── Empty State Nudges ──
        if (np.engagement && taskSnap.size === 0) {
          const dedupKey = `nudge_noTasks_${uid}_${weekKey}`;
          if (!localStorage.getItem(dedupKey)) {
            addNotification(
              "Get Started!",
              "You don't have any tasks yet \u2014 create one!",
              "nudge"
            );
            localStorage.setItem(dedupKey, "true");
          }
        }

        if (np.engagement && noteSnap.size === 0) {
          const dedupKey = `nudge_noNotes_${uid}_${weekKey}`;
          if (!localStorage.getItem(dedupKey)) {
            addNotification(
              "Start Writing!",
              "Create your first note to get organized.",
              "nudge"
            );
            localStorage.setItem(dedupKey, "true");
          }
        }

        // ── Motivational / Streaks ──
        if (np.engagement) {
          const tasks = taskSnap.docs.map((d) => ({
            docId: d.id,
            ...d.data(),
          })) as TaskDoc[];

          // Completed this week (Monday-based)
          const now = new Date();
          const jsDay = now.getDay();
          const monday = new Date(now);
          monday.setDate(now.getDate() - (jsDay === 0 ? 6 : jsDay - 1));
          monday.setHours(0, 0, 0, 0);
          const completedThisWeek = tasks.filter((t) => {
            if (!t.toggle || !t.completedDate) return false;
            return new Date(t.completedDate) >= monday;
          }).length;

          if (completedThisWeek >= 20) {
            const dedupKey = `streak_weekly20_${uid}_${weekKey}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification("Productivity Champion!", `You've completed ${completedThisWeek} tasks this week! Incredible!`, "streak");
              localStorage.setItem(dedupKey, "true");
            }
          } else if (completedThisWeek >= 10) {
            const dedupKey = `streak_weekly10_${uid}_${weekKey}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification("Amazing Progress!", `You've completed ${completedThisWeek} tasks this week!`, "streak");
              localStorage.setItem(dedupKey, "true");
            }
          } else if (completedThisWeek >= 5) {
            const dedupKey = `streak_weekly5_${uid}_${weekKey}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification("Great Progress!", `You've completed ${completedThisWeek} tasks this week! Keep going!`, "streak");
              localStorage.setItem(dedupKey, "true");
            }
          }

          // Day streak
          const streakDays = calculateStreak(tasks);
          if (streakDays >= 14) {
            const dedupKey = `streak_days14_${uid}_${dayKey}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification("Unstoppable!", `${streakDays} day streak! You're on a roll!`, "streak");
              localStorage.setItem(dedupKey, "true");
            }
          } else if (streakDays >= 7) {
            const dedupKey = `streak_days7_${uid}_${dayKey}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification("On Fire!", `${streakDays} day streak! Keep it going!`, "streak");
              localStorage.setItem(dedupKey, "true");
            }
          } else if (streakDays >= 3) {
            const dedupKey = `streak_days3_${uid}_${dayKey}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification("Nice Streak!", `${streakDays} day streak! Don't break it!`, "streak");
              localStorage.setItem(dedupKey, "true");
            }
          }
        }

        // ── Feature Announcements ──
        if (np.announcements) try {
          const announcementSnap = await getDocs(
            query(
              dbAnnouncements,
              where("active", "==", true),
              orderBy("createdAt", "desc"),
              limit(5)
            )
          );

          for (const doc of announcementSnap.docs) {
            const data = doc.data();
            const dedupKey = `announcement_${doc.id}_${uid}`;
            if (!localStorage.getItem(dedupKey)) {
              addNotification(
                data.title || "New Update",
                data.body || "",
                "announcement"
              );
              localStorage.setItem(dedupKey, "true");
            }
          }
        } catch (err) {
          // Announcements collection may not exist yet — that's fine
          console.debug("[Engagement] Announcements query failed (collection may not exist):", err);
        }
      } catch (err) {
        console.error("[Engagement] Error checking engagement notifications:", err);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [userDetails, settings.notifications, addNotification]);
};

export default useEngagementNotifications;
